import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        // Build the baseURL from the configured API URL and version. This
        // allows the API version to be changed via environment variables
        // without touching the service layer. See Constants.ts for details.
        const base = `${config.apiUrl}`
        apiInstance = axios.create({
            baseURL: base,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export interface Bundle {
    id: string
    title: string
    description: string
    full_price: number
    discount_price?: number
    course_ids: string[]
    is_active: boolean
    hidden: boolean
    thumbnail_url?: string
    savings_percentage?: number
    created_at: string
    updated_at: string
}

export interface BundleQueryParams {
    page?: number
    page_size?: number
    is_active?: boolean
    hidden?: boolean
}

export interface BundleListData {
    bundles: Bundle[]
    total?: number
    page?: number
    page_size?: number
}

export const bundleService = {
    getAllBundles,
    getBundleById,
    createBundle,
    updateBundle,
    deleteBundle,
    toggleBundleStatus,
}

/**
 * Get all bundles with optional filters
 */
async function getAllBundles(params?: BundleQueryParams): Promise<ApiResponse<BundleListData>> {
    try {
        const response = await getApiInstance().get('/bundles', { params })
        
        // Handle wrapped response from backend (ResponseModel)
        let bundles: Bundle[] = []
        let total: number | undefined
        let page: number | undefined
        let page_size: number | undefined

        if (response.data?.success && response.data?.data) {
            bundles = response.data.data.bundles || []
            total = response.data.data.total
            page = response.data.data.page
            page_size = response.data.data.page_size
        } else if (response.data?.bundles) {
            bundles = response.data.bundles
            total = response.data.total
            page = response.data.page
            page_size = response.data.page_size
        } else if (response.data?.data) {
            bundles = response.data.data
        } else {
            bundles = []
        }

        // Ensure course_ids is always an array for each bundle
        bundles = bundles.map(bundle => ({
            ...bundle,
            course_ids: Array.isArray(bundle.course_ids) ? bundle.course_ids : []
        }))

        return {
            success: true,
            data: { bundles, total, page, page_size }
        }
    } catch (error: any) {
        console.error('Error fetching bundles:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch bundles'
        }
    }
}

/**
 * Get a single bundle by ID
 */
async function getBundleById(id: string): Promise<ApiResponse<Bundle>> {
    try {
        const response = await getApiInstance().get(`/bundles/${id}`)
        let bundle = response.data?.data || response.data

        if (!bundle) {
            return {
                success: false,
                error: 'Bundle not found'
            }
        }

        // Ensure course_ids is always an array
        bundle = {
            ...bundle,
            course_ids: Array.isArray(bundle.course_ids) ? bundle.course_ids : []
        }

        return {
            success: true,
            data: bundle
        }
    } catch (error: any) {
        console.error('Error fetching bundle:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch bundle'
        }
    }
}

/**
 * Create a new bundle
 */
async function createBundle(data: Partial<Bundle>): Promise<ApiResponse<Bundle>> {
    try {
        const response = await getApiInstance().post('/bundles', data)
        const bundle = response.data?.data || response.data
        
        return {
            success: true,
            data: bundle
        }
    } catch (error: any) {
        console.error('Error creating bundle:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to create bundle'
        }
    }
}

/**
 * Update an existing bundle
 */
async function updateBundle(id: string, data: Partial<Bundle>): Promise<ApiResponse<Bundle>> {
    try {
        const response = await getApiInstance().put(`/bundles/${id}`, data)
        const bundle = response.data?.data || response.data
        
        return {
            success: true,
            data: bundle
        }
    } catch (error: any) {
        console.error('Error updating bundle:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update bundle'
        }
    }
}

/**
 * Delete a bundle
 */
async function deleteBundle(id: string): Promise<ApiResponse<void>> {
    try {
        await getApiInstance().delete(`/bundles/${id}`)
        
        return {
            success: true,
            data: undefined
        }
    } catch (error: any) {
        console.error('Error deleting bundle:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete bundle'
        }
    }
}

/**
 * Toggle bundle active status
 */
async function toggleBundleStatus(id: string, isActive: boolean): Promise<ApiResponse<Bundle>> {
    try {
        const response = await getApiInstance().put(`/bundles/${id}`, { is_active: isActive })
        const bundle = response.data?.data || response.data
        
        return {
            success: true,
            data: bundle
        }
    } catch (error: any) {
        console.error('Error toggling bundle status:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to toggle bundle status'
        }
    }
}
